/**
 * TestNumberForm — create/edit dialog body for `/admin/test-numbers` (Story 9.12).
 *
 * On create: phone + label required.
 * On edit: phone is shown read-only (immutable contract); only label editable.
 */

import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface TestNumberFormValue {
  phone: string;
  label: string;
}

const DEFAULT: TestNumberFormValue = { phone: '', label: '' };

interface Props {
  mode: 'create' | 'edit';
  initial?: TestNumberFormValue;
  submitting?: boolean;
  onSubmit: (next: TestNumberFormValue) => void;
  onCancel: () => void;
}

/** Reject inputs that won't pass the server's normalization (min 8 digits). */
function validatePhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return 'Telefone obrigatório.';
  if (digits.length < 8) return 'Telefone precisa ter ao menos 8 dígitos.';
  return null;
}

function validateLabel(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 'Rótulo obrigatório.';
  if (trimmed.length > 100) return 'Rótulo deve ter no máximo 100 caracteres.';
  return null;
}

export function TestNumberForm({ mode, initial, submitting, onSubmit, onCancel }: Props) {
  const [form, setForm] = useState<TestNumberFormValue>(initial ?? DEFAULT);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);

  function handlePhoneChange(next: string) {
    setForm((prev) => ({ ...prev, phone: next }));
    setPhoneError(next === '' ? null : validatePhone(next));
  }

  function handleLabelChange(next: string) {
    setForm((prev) => ({ ...prev, label: next }));
    setLabelError(next === '' ? null : validateLabel(next));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const pErr = mode === 'create' ? validatePhone(form.phone) : null;
    const lErr = validateLabel(form.label);
    setPhoneError(pErr);
    setLabelError(lErr);
    if (pErr || lErr) return;
    onSubmit(form);
  }

  const disabled =
    !!submitting ||
    !!labelError ||
    form.label.trim().length === 0 ||
    (mode === 'create' && (!!phoneError || form.phone.trim().length === 0));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="test-number-phone">Telefone (formato E.164)</Label>
        <Input
          id="test-number-phone"
          aria-label="Telefone"
          value={form.phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          placeholder="+5511999999999"
          maxLength={32}
          readOnly={mode === 'edit'}
          disabled={mode === 'edit'}
        />
        {mode === 'edit' ? (
          <p className="text-xs text-muted-foreground">
            Telefone é imutável após cadastro. Para trocar, desative este e crie um novo.
          </p>
        ) : null}
        {phoneError ? (
          <p className="text-sm text-destructive" role="alert">
            {phoneError}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="test-number-label">Rótulo (quem é esse número?)</Label>
        <Input
          id="test-number-label"
          aria-label="Rótulo"
          value={form.label}
          onChange={(e) => handleLabelChange(e.target.value)}
          placeholder="Victor — celular pessoal"
          maxLength={100}
        />
        {labelError ? (
          <p className="text-sm text-destructive" role="alert">
            {labelError}
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={disabled}>
          {mode === 'create' ? 'Cadastrar' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
